import { Box, Flex, Text, useDisclosure, useTheme } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts';
import { KeyResultViewDetails } from './KeyResultViewDetails';
import { RightIcon } from '../icons';
import { splitTime } from '../shared/utils';
import { ChartDataEntry, MetricOperator } from './types';

const CustomTooltip = ({
  active,
  payload,
  label,
  onButtonClick,
  scope,
}: any) => {
  const { colors } = useTheme();
  if (active && payload && payload.length) {
    const { achieved, status, startDate, endDate } = payload[0].payload;

    return (
      <Box
        bg="white"
        p={3}
        boxShadow="md"
        borderRadius="md"
        border="1px solid"
        borderColor="text.tertiary"
        minW={'184px'}
      >
        <Text
          fontSize="xs"
          fontWeight="medium"
          mb={'4px'}
          color={'text.secondary2'}
        >
          {label}
        </Text>

        <Box
          display="flex"
          justifyContent="space-between"
          mb={'8px'}
          borderTop={'1px solid'}
          borderColor={'text.tertiary'}
          pt={'4px'}
          fontSize={'xs'}
          fontWeight={'medium'}
        >
          <Text>Achieved:</Text>
          <Text fontWeight={'semibold'}>{achieved}</Text>
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          mb={'8px'}
          fontSize={'xs'}
          fontWeight={'medium'}
        >
          <Text mr={1}>Target: </Text>
          <Text fontWeight={'semibold'}>
            {payload[0].payload.metricOperator === 'LESS_THAN' && '< '}
            {payload[0].payload.metricOperator === 'GREATER_THAN' && '> '}
            {payload[0].payload.metricOperator === 'BETWEEN'
              ? `${payload[0].payload.target} - ${payload[0].payload.maxTarget}`
              : payload[0].payload.unit !== 'Minutes'
              ? payload[0].payload.target
              : splitTime((payload[0].payload.target || 0) / 60)}{' '}
            {payload[0].payload.unit !== 'Minutes' && (
              <Text as={'span'} fontSize={'xs'}>
                ({payload[0].payload.unit})
              </Text>
            )}
          </Text>
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          fontSize={'xs'}
          fontWeight={'medium'}
        >
          <Text>Status:</Text>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            bg={status === 'ACHIEVED' ? '#E6F4EA' : '#FFECE6'}
            color={status === 'ACHIEVED' ? '#1DA44A' : '#E65100'}
            px={2}
            py={0.5}
            borderRadius="md"
          >
            {status}
          </Text>
        </Box>
        {scope !== 'CONTRIBUTOR' && (
          <Flex
            fontSize="xs"
            mt={2}
            onClick={(e) => {
              e.stopPropagation();
              onButtonClick && onButtonClick({ startDate, endDate });
            }}
            onMouseDown={(e) => e.stopPropagation()}
            color={'text.secondary2'}
            fontWeight={'medium'}
            cursor={'pointer'}
            gap={1}
            align={'center'}
          >
            View Details
            <RightIcon
              height="12px"
              width="12px"
              color={colors.text.secondary2}
              strokeWidth="3"
            />
          </Flex>
        )}
      </Box>
    );
  }
  return null;
};

const CustomReferenceLabel = ({ viewBox, value }: any) => {
  return (
    <g transform={`translate(${viewBox.x + 50},${viewBox.y})`}>
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="1" stdDeviation="2" floodOpacity="0.2" />
        </filter>
      </defs>
      <rect
        x="-50"
        y="-12"
        width="100"
        height="24"
        rx="3"
        fill="#4A4A4A"
        filter="url(#shadow)"
      />
      <text
        x="0"
        y="4"
        textAnchor="middle"
        fill="#FFFFFF"
        fontSize="12px"
        fontWeight="medium"
      >
        {value}
      </text>
    </g>
  );
};

interface KeyResultsBarChartProps {
  data: string;
  targetValue: string;
  metricOperator: MetricOperator;
  unit: string;
  metricKey: string;
  metricTitle: string;
  scope: string;
  keyResultTitle: string;
}

const CustomBarChart = ({
  data: rawData,
  targetValue,
  metricOperator,
  unit,
  metricKey,
  metricTitle,
  scope,
  keyResultTitle,
}: KeyResultsBarChartProps) => {
  const getTargetValues = useMemo(() => {
    if (!targetValue) return { min: 0, max: null };

    if (targetValue.includes('-') && metricOperator === 'BETWEEN') {
      const [min, max] = targetValue
        .split('-')
        .map((val) => parseFloat(val.trim()));
      return { min, max };
    } else {
      return { min: parseFloat(targetValue), max: null };
    }
  }, [targetValue, metricOperator]);

  const parsedData = useMemo((): ChartDataEntry[] => {
    if (!rawData) return [];

    try {
      const parsed = JSON.parse(rawData);
      const { min, max } = getTargetValues;

      return parsed.map((item: any): ChartDataEntry => {
        const achievedValue = item.y;

        return {
          name: item.xlabelName,
          achieved: achievedValue,
          target: min,
          maxTarget: max,
          status: item.status,
          startDate: item.startDate,
          endDate: item.endDate,
          metricOperator,
          unit,
        };
      });
    } catch (error) {
      return [];
    }
  }, [rawData, metricOperator, getTargetValues]);

  const getBarColor = (entry: ChartDataEntry) => {
    return entry.status === 'ACHIEVED' ? '#4D4DFF' : '#FF9D66';
  };

  const getMinTargetLabel = () => {
    const { min } = getTargetValues;

    switch (metricOperator) {
      case 'LESS_THAN':
        return `Target: < ${unit === 'Minutes' ? splitTime(min / 60) : min}`;
      case 'BETWEEN':
        return `Min: ${unit === 'Minutes' ? splitTime(min / 60) : min}`;
      case 'GREATER_THAN':
      default:
        return `Target: > ${unit === 'Minutes' ? splitTime(min / 60) : min}`;
    }
  };

  const getMaxTargetLabel = () => {
    const { max } = getTargetValues;
    return `Max: ${max}`;
  };

  const { onOpen, onClose, isOpen } = useDisclosure();
  const [selectedBarStartDate, setSelectedBarStartDate] = useState('');
  const [selectedBarEndDate, setSelectedBarEndDate] = useState('');
  const handleBarClick = (data: any, index: number) => {
    setSelectedBarStartDate(data.startDate);
    setSelectedBarEndDate(data.endDate);
    onOpen();
  };
  return (
    <>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={parsedData}
          margin={{ top: 20, right: 30, left: -16, bottom: 5 }}
          barSize={40}
          barCategoryGap={'30%'}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />

          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#696F8C', dy: 10 }}
            interval="preserveStartEnd"
            minTickGap={20}
          />
          <YAxis
            domain={[0, 'dataMax + 1']}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#696F8C' }}
            tickFormatter={(value) => Math.round(value).toString()}
          />
          <Tooltip
            content={
              <CustomTooltip onButtonClick={handleBarClick} scope={scope} />
            }
            cursor={{ fill: 'transparent' }}
            allowEscapeViewBox={{ x: false, y: false }}
            wrapperStyle={{ pointerEvents: 'auto' }}
          />

          <Bar
            dataKey="achieved"
            name="Achieved"
            cursor={scope !== 'CONTRIBUTOR' ? 'pointer' : 'default'}
            onClick={scope !== 'CONTRIBUTOR' ? handleBarClick : undefined}
          >
            {parsedData?.map((entry: ChartDataEntry, index: number) => (
              <Cell key={`cell-${index}`} fill={getBarColor(entry)} />
            ))}
          </Bar>

          <ReferenceLine
            y={getTargetValues.min}
            stroke="#000"
            strokeDasharray="3 3"
            label={{
              value: getMinTargetLabel(),
              position: 'left',
              content: CustomReferenceLabel,
            }}
            ifOverflow="extendDomain"
          />

          {metricOperator === 'BETWEEN' && getTargetValues.max && (
            <ReferenceLine
              y={getTargetValues.max}
              stroke="#000"
              strokeDasharray="3 3"
              label={{
                value: getMaxTargetLabel(),
                position: 'left',
                content: CustomReferenceLabel,
              }}
              ifOverflow="extendDomain"
            />
          )}
        </BarChart>
      </ResponsiveContainer>
      {isOpen && (
        <KeyResultViewDetails
          isOpen={isOpen}
          onClose={onClose}
          metricKey={metricKey}
          metricTitle={metricTitle}
          goalStartDate={selectedBarStartDate}
          goalEndDate={selectedBarEndDate}
          scope={scope}
          keyResultTitle={keyResultTitle}
        />
      )}
    </>
  );
};

export default CustomBarChart;
